import { TypedDocumentNode, gql } from '@apollo/client';
import { isAfter } from 'date-fns';
import styled from 'styled-components';

import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Missions } from '@sorare/core/src/atoms/icons/Missions';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isType } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';

import { DailyChallenge } from 'components/rivals/MissionsPage/DailyChallenges/DailyChallenge';

import {
  RivalsDraftDailyMissionsQuery,
  RivalsDraftDailyMissionsQueryVariables,
} from './__generated__/index.graphql';

const Challenges = styled(Vertical)`
  background-color: var(--c-neutral-200);

  & > :not(:last-child) {
    border-radius: 0;
    border-bottom: 1px solid var(--c-neutral-300);
  }
`;

const RIVALS_DRAFT_DAILY_MISSIONS_QUERY = gql`
  query RivalsDraftDailyMissionsQuery {
    currentUser {
      slug
      managerProgression(sport: FOOTBALL) {
        id
        categories(type: RIVALS_DAILY) {
          id
          categoryTasks {
            ... on ManagerTaskInterface {
              id
            }
            ... on ManagerProgressionMission {
              id
              ...DailyChallenge_managerProgressionMission
            }
          }
        }
      }
    }
  }
  ${DailyChallenge.fragments.managerProgressionMission}
` as TypedDocumentNode<
  RivalsDraftDailyMissionsQuery,
  RivalsDraftDailyMissionsQueryVariables
>;

type Props = { gameStartsAt?: Date };
export const DailyMissions = ({ gameStartsAt }: Props) => {
  const { data } = useQuery(RIVALS_DRAFT_DAILY_MISSIONS_QUERY);

  type MissionTask = NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<RivalsDraftDailyMissionsQuery>['currentUser']
      >['managerProgression']
    >['categories']
  >[number]['categoryTasks'][number] & {
    __typename: 'ManagerProgressionMission';
    id: string;
  };
  const challenges = (data?.currentUser?.managerProgression?.categories || [])
    .flatMap(({ categoryTasks }) => categoryTasks)
    .filter(
      (task): task is MissionTask =>
        isType(task, 'ManagerProgressionMission') &&
        (!gameStartsAt || isAfter(task.cutoffAt, gameStartsAt))
    );

  if (!challenges.length) {
    return null;
  }

  return (
    <Dropdown
      triggerOnHover
      label={props => (
        <IconButton {...props} color="quaternary">
          <Missions width={16} height={16} />
        </IconButton>
      )}
      gap={8}
      collisionPadding={16}
    >
      <Challenges>
        {challenges?.map(challenge => (
          <DailyChallenge key={challenge.id} task={challenge} disableClaim />
        ))}
      </Challenges>
    </Dropdown>
  );
};
